/** Assembles the flat ViewModel the generated views bind to — the reference's renderVals(), split into slices. */
import type { DemoData } from '@pointfive/contracts';
import type { ViewModel } from '@/screens/generated/view-model';
import type { AppProps, Ctx, Patch } from './ctx';
import { chart, dayLabel, dur, fmt, fmtP, tog, trades } from './helpers';
import { pfEngine } from './pfEngine';
import { SCREEN_MODE } from './nav';
import type { ModelState, ScreenId, ToastKind } from './state';
import { buildCore } from './core';
import { buildSlices } from './slices';

export interface BuildInput {
  S: ModelState;
  set(patch: Patch): void;
  props: AppProps;
  data: DemoData;
  timers: Ctx['timers'];
  /** The tooltip element. Owned by the host component, because it must survive re-renders to be positioned.
   *  Optional so a model test can build a context without a DOM: a ref pointing at nothing simply positions
   *  nothing, which is the correct behaviour when there is no tooltip on screen. */
  tipRef?: Ctx['tipRef'];
}
export function createCtx(input: BuildInput): Ctx {
  const { S, set, props, data, timers } = input;
  const c: Ctx = {
    S,
    set,
    props,
    data,
    timers,
    tipRef: input.tipRef ?? { current: null },
    flash(msg: string, kind?: ToastKind) {
      set({ toast: msg, toastKind: kind || 'pos' });
      timers.after(3200, () => set({ toast: '' }), 'toast');
    },
    go(id: ScreenId) {
      set({ screen: id, mode: SCREEN_MODE[id] ?? S.mode });
    },
    fmt,
    fmtP,
    dur,
    dayLabel,
    tog,
    chart: () => chart(S),
    trades: () => trades(data.core),
    pfEngine: () => pfEngine(S, data.score),
  };
  return c;
}
export function buildViewModel(input: BuildInput): ViewModel {
  const c = createCtx(input);
  return { ...buildCore(c), ...buildSlices(c) } as ViewModel;
}
